import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Users,
  CheckCircle2,
  UserCheck,
  Eye,
  RefreshCw,
  AlertCircle,
  TrendingUp,
  PlusCircle,
  MinusCircle,
  Edit2,
  WifiOff,
  Trash2,
  X,
  Save,
  LogOut,
  Building2,
  ClipboardCheck
} from 'lucide-react';

const AdminLiveDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('online'); // online, offline, slow
  const [lastUpdated, setLastUpdated] = useState(null);
  const [tokenRefreshing, setTokenRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [checkingIn, setCheckingIn] = useState(new Set());
  // Get current date in Adelaide timezone
  const getAdelaideDate = () => {
    const now = new Date();
    // Use Intl.DateTimeFormat for more reliable timezone conversion
    const formatter = new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Adelaide',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const parts = formatter.formatToParts(now);
    const year = parts.find(p => p.type === 'year').value;
    const month = parts.find(p => p.type === 'month').value;
    const day = parts.find(p => p.type === 'day').value;
    return `${year}-${month}-${day}`;
  };
  
  const [selectedDate, setSelectedDate] = useState(() => getAdelaideDate());
  const navigate = useNavigate();
  
  // Manual tour logging state
  const [showManualEntryForm, setShowManualEntryForm] = useState(false);
  const [selectedTourSource, setSelectedTourSource] = useState(null); // 'oasis', 'noble', 'manual'
  const [manualEntry, setManualEntry] = useState({
    company_name: '',
    date: getAdelaideDate(),
    time: '',
    number_of_people: '',
    crew_name: '',
    crew_no: '',
    staff_member: '',
    umoona_staff: '',
    notes: '',
    tour_source: '',
    tour_types: [],
    extra_tour_reason: ''
  });
  const [submittingManualEntry, setSubmittingManualEntry] = useState(false);
  const [manualTours, setManualTours] = useState([]);
  const [editingTourId, setEditingTourId] = useState(null);

  // Generate time options (8AM-6PM in 15-min increments)
  const timeOptions = [];
  for (let hour = 8; hour <= 18; hour++) {
    for (let minute of [0, 15, 30, 45]) {
      if (hour === 18 && minute > 0) break;
      const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const label = `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
      timeOptions.push({ value: time24, label });
    }
  }

  // Tour type options
  const tourTypeOptions = [
    { value: 'full_tour', label: 'Full Tour' },
    { value: 'tour_without_movie', label: 'Tour without Movie' },
    { value: 'cutting_demonstration', label: 'Cutting Demo' },
    { value: 'ext_town_tour', label: 'Ext Town Tour' },
    { value: 'town_tour', label: 'Town Tour' },
    { value: 'no_town_tour', label: 'No Town Tour' },
    { value: 'samc', label: 'SAMC' },
    { value: 'kanku', label: 'Kanku' },
    { value: 'linen', label: 'Linen' },
    { value: 'linen_with_towels', label: 'Linen + Towels' },
    { value: 'no_church', label: 'No Church' },
    { value: 'serbian_church', label: 'Serbian Church' },
    { value: 'catholic_church', label: 'Catholic Church' }
  ];

  // Tour source colors and labels
  const tourSourceConfig = {
    oasis: { label: 'Oasis Tour', color: 'emerald', bgClass: 'bg-emerald-600', hoverClass: 'hover:bg-emerald-700', lightBg: 'bg-emerald-50', textClass: 'text-emerald-700', borderClass: 'border-emerald-200' },
    noble: { label: 'Noble Tour', color: 'sky', bgClass: 'bg-sky-600', hoverClass: 'hover:bg-sky-700', lightBg: 'bg-sky-50', textClass: 'text-sky-700', borderClass: 'border-sky-200' },
    manual: { label: 'Manual Entry', color: 'purple', bgClass: 'bg-purple-600', hoverClass: 'hover:bg-purple-700', lightBg: 'bg-purple-50', textClass: 'text-purple-700', borderClass: 'border-purple-200' }
  };
  
  // Inline editing state
  const [editingField, setEditingField] = useState(null); // { tourId, field }
  const [editingValue, setEditingValue] = useState('');
  const [savingField, setSavingField] = useState(null);
  
  // Staff assignment state
  const [staffAssignments, setStaffAssignments] = useState({}); // { "10:00": "John Smith", "14:00": "Jane Doe" }
  const [editingStaff, setEditingStaff] = useState(null); // timeSlot being edited
  const [tempStaffName, setTempStaffName] = useState('');

  // Auto refresh every 3 seconds - Safari/iOS compatible
  useEffect(() => {
    let isMounted = true;
    let timeoutId = null;
    let lastRefreshTime = Date.now();

    const refreshData = async () => {
      if (!isMounted) return;
      
      const now = Date.now();
      const timeSinceLastRefresh = now - lastRefreshTime;
      
      // Only refresh if at least 2.5 seconds have passed (avoid rapid fire)
      if (timeSinceLastRefresh >= 2500) {
        console.log('Auto-refresh triggered at', new Date().toLocaleTimeString());
        lastRefreshTime = now;
        
        try {
          // Run all fetches in parallel with error handling
          await Promise.allSettled([
            fetchDashboardData(false),
            fetchManualTours(),
            fetchStaffAssignments()
          ]);
        } catch (err) {
          console.error('Refresh error:', err);
        }
      }

      // Schedule next refresh - Safari-friendly recursive timeout
      if (isMounted) {
        timeoutId = setTimeout(refreshData, 3000);
      }
    };

    // Start the refresh loop
    timeoutId = setTimeout(refreshData, 3000);

    // Handle tab visibility changes (Safari pauses timers in background tabs)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Tab became visible - refreshing immediately');
        lastRefreshTime = 0; // Force immediate refresh
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(refreshData, 100); // Refresh after 100ms
      } else {
        console.log('Tab hidden - timers may be throttled');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also handle page focus/blur (iOS Safari specific)
    const handleFocus = () => {
      console.log('Page focused - refreshing');
      lastRefreshTime = 0;
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(refreshData, 100);
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [selectedDate]);

  // Fetch manual tours and staff assignments when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchManualTours();
      fetchStaffAssignments();
    }
  }, [selectedDate]);

  // Monitor network connection status
  useEffect(() => {
    const handleOnline = () => {
      console.log('Network: Online');
      setConnectionStatus('online');
      setError('');
      // Immediately refresh data when coming back online
      fetchDashboardData(false);
      fetchManualTours();
      fetchStaffAssignments();
    };

    const handleOffline = () => {
      console.log('Network: Offline');
      setConnectionStatus('offline');
      setError('No internet connection - showing last loaded data');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check initial connection status
    if (!navigator.onLine) {
      setConnectionStatus('offline');
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Safari iOS Wake Lock - prevent screen from sleeping
  useEffect(() => {
    let wakeLock = null;

    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen');
          console.log('Wake Lock acquired - screen will stay on');
          
          wakeLock.addEventListener('release', () => {
            console.log('Wake Lock released');
          });
        }
      } catch (err) {
        console.log('Wake Lock not available:', err.message);
      }
    };

    // Request wake lock when page becomes visible
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && wakeLock === null) {
        requestWakeLock();
      }
    };

    requestWakeLock();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      if (wakeLock) {
        wakeLock.release().then(() => {
          wakeLock = null;
        });
      }
    };
  }, []);

  // Auto-refresh token every 12 hours to keep login alive indefinitely
  useEffect(() => {
    // Refresh token on mount
    refreshAuthToken();

    // Set up interval to refresh every 12 hours (12 * 60 * 60 * 1000 ms)
    const tokenRefreshInterval = setInterval(() => {
      console.log('⏰ Scheduled token refresh - keeping login alive');
      refreshAuthToken();
    }, 12 * 60 * 60 * 1000); // 12 hours

    return () => {
      clearInterval(tokenRefreshInterval);
    };
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [selectedDate]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return null;
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    };
  };

  // Helper function to add cache-busting timestamp to URLs
  const addCacheBuster = (url) => {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}_t=${Date.now()}`;
  };

  // Logout function
  const handleLogout = () => {
    console.log('🚪 User logged out');
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  // Refresh authentication token to keep login alive
  const refreshAuthToken = async () => {
    if (tokenRefreshing) return; // Prevent concurrent refreshes
    
    try {
      setTokenRefreshing(true);
      const token = localStorage.getItem('adminToken');
      if (!token) {
        console.warn('No token to refresh - redirecting to login');
        navigate('/admin/login');
        return false;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/refresh-token`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('adminToken', data.access_token);
        console.log('✅ Token refreshed successfully - login extended');
        return true;
      } else if (response.status === 401) {
        console.warn('Token expired - redirecting to login');
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
        return false;
      } else {
        console.error('Token refresh failed:', response.status);
        return false;
      }
    } catch (err) {
      console.error('Error refreshing token:', err);
      return false;
    } finally {
      setTokenRefreshing(false);
    }
  };

  // Fetch with timeout (Safari-friendly)
  const fetchWithTimeout = async (url, options = {}, timeout = 10000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please check your connection');
      }
      throw error;
    }
  };

  const fetchDashboardData = async (showLoading = true, retryCount = 0) => {
    try {
      if (showLoading) setLoading(true);
      const headers = getAuthHeaders();
      if (!headers) {
        console.warn('No auth headers - redirecting to login');
        return;
      }

      const url = addCacheBuster(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/live-dashboard/${selectedDate}`);
      const response = await fetchWithTimeout(url, { 
        headers,
        cache: 'no-store'
      }, 10000); // 10 second timeout

      if (response.status === 401) {
        if (retryCount === 0) {
          // Token expired - try to refresh and retry once
          console.log('🔄 401 error - attempting token refresh');
          const refreshed = await refreshAuthToken();
          if (refreshed) {
            console.log('✅ Token refreshed - retrying request');
            return await fetchDashboardData(showLoading, 1); // Retry once with new token
          }
        }
        
        // Token refresh failed or already retried - redirect to login
        console.error('❌ Session expired - redirecting to login');
        setError('Your session has expired. Redirecting to login...');
        localStorage.removeItem('adminToken');
        setTimeout(() => {
          navigate('/admin/login');
        }, 2000); // Show message for 2 seconds before redirecting
        return;
      }

      if (!response.ok) {
        console.error('Dashboard fetch failed:', response.status, response.statusText);
        throw new Error(`Failed to fetch dashboard data (${response.status})`);
      }

      const data = await response.json();
      setDashboardData(data);
      setError(''); // Clear any previous errors
      setLastUpdated(new Date()); // Track last successful update
    } catch (err) {
      console.error('fetchDashboardData error:', err);
      setError(err.message);
      // Don't clear dashboardData on error - keep showing last good data
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const handleCheckIn = async (bookingId, customerName) => {
    if (checkingIn.has(bookingId)) return;

    try {
      setCheckingIn(prev => new Set(prev).add(bookingId));
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/${bookingId}/check-in`,
        {
          method: 'POST',
          headers
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update check-in status');
      }

      // Refresh dashboard data
      await fetchDashboardData(false);
      
    } catch (err) {
      setError(`Failed to check in ${customerName}: ${err.message}`);
    } finally {
      setCheckingIn(prev => {
        const newSet = new Set(prev);
        newSet.delete(bookingId);
        return newSet;
      });
    }
  };

  const handleManualAdjustment = async (timeSlot, adjustment, bookingSource = 'walkin') => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/tours/manual-adjustment`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            tour_date: selectedDate,
            tour_time: timeSlot,
            adjustment: adjustment,
            booking_source: bookingSource
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to adjust tickets');
      }

      // Refresh dashboard data
      await fetchDashboardData(false);
      
    } catch (err) {
      setError(`Failed to adjust tickets: ${err.message}`);
    }
  };

  // Fetch manual tours for selected date
  const fetchManualTours = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) {
        console.warn('No auth headers for manual tours');
        return;
      }

      const url = addCacheBuster(`${process.env.REACT_APP_BACKEND_URL}/api/admin/manual-tours/${selectedDate}`);
      const response = await fetchWithTimeout(url, { 
        headers,
        cache: 'no-store'
      }, 10000); // 10 second timeout

      if (response.status === 401) {
        console.error('❌ Session expired - redirecting to login');
        setError('Your session has expired. Redirecting to login...');
        localStorage.removeItem('adminToken');
        setTimeout(() => {
          navigate('/admin/login');
        }, 2000);
        return;
      }

      if (response.ok) {
        const tours = await response.json();
        setManualTours(tours || []); // Ensure it's always an array
      } else {
        console.error('Manual tours fetch failed:', response.status);
        // Don't clear tours on error - keep showing last good data
      }
    } catch (err) {
      console.error('Error fetching manual tours:', err);
      // Don't clear tours on error - keep showing last good data
    }
  };

  // Fetch staff assignments for selected date from database
  const fetchStaffAssignments = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) {
        console.warn('No auth headers for staff assignments');
        return;
      }

      const url = addCacheBuster(`${process.env.REACT_APP_BACKEND_URL}/api/admin/staff-assignments/${selectedDate}`);
      const response = await fetchWithTimeout(url, { 
        headers,
        cache: 'no-store'
      }, 10000); // 10 second timeout

      if (response.status === 401) {
        console.error('❌ Session expired - redirecting to login');
        setError('Your session has expired. Redirecting to login...');
        localStorage.removeItem('adminToken');
        setTimeout(() => {
          navigate('/admin/login');
        }, 2000);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setStaffAssignments(data.assignments || {});
      } else {
        console.error('Staff assignments fetch failed:', response.status);
        // Don't clear assignments on error - keep showing last good data
      }
    } catch (err) {
      console.error('Error fetching staff assignments:', err);
      // Don't clear assignments on error - keep showing last good data
    }
  };

  // Handle manual entry submission (create or update)
  const handleManualEntrySubmit = async (e) => {
    e.preventDefault();

    if (!manualEntry.date || !manualEntry.time || !manualEntry.number_of_people) {
      setError('Please fill in all required fields for manual tour entry');
      return;
    }

    if (!selectedTourSource) {
      setError('Please select a tour source (Oasis, Noble, or Manual)');
      return;
    }

    // Require extra_tour_reason for manual entries
    if (selectedTourSource === 'manual' && !manualEntry.extra_tour_reason) {
      setError('Please provide a reason for this extra tour');
      return;
    }

    setSubmittingManualEntry(true);
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const payload = {
        company_name: manualEntry.company_name || null,
        tour_date: manualEntry.date,
        tour_time: manualEntry.time,
        guest_count: parseInt(manualEntry.number_of_people),
        crew_name: manualEntry.crew_name || null,
        crew_no: manualEntry.crew_no || null,
        tour_source: selectedTourSource,
        tour_types: manualEntry.tour_types || [],
        extra_tour_reason: selectedTourSource === 'manual' ? manualEntry.extra_tour_reason : null,
        umoona_staff: manualEntry.umoona_staff || null,
        staff_member: manualEntry.staff_member || '',
        notes: manualEntry.notes || ''
      };

      const url = editingTourId
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/manual-tours/${editingTourId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/manual-tours`;

      const method = editingTourId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to ${editingTourId ? 'update' : 'log'} tour`);
      }

      // Success - reset form
      setManualEntry({
        company_name: '',
        date: getAdelaideDate(),
        time: '',
        number_of_people: '',
        crew_name: '',
        crew_no: '',
        staff_member: '',
        umoona_staff: '',
        notes: '',
        tour_source: '',
        tour_types: [],
        extra_tour_reason: ''
      });
      setEditingTourId(null);
      setSelectedTourSource(null);
      setShowManualEntryForm(false);
      setError('');

      // Refresh manual tours list
      await fetchManualTours();

      // Refresh dashboard if viewing the same date
      if (manualEntry.date === selectedDate) {
        await fetchDashboardData(false);
      }

    } catch (err) {
      setError(`Failed to ${editingTourId ? 'update' : 'log'} tour: ${err.message}`);
    } finally {
      setSubmittingManualEntry(false);
    }
  };

  // Handle tour type checkbox toggle
  const handleTourTypeChange = (tourType, checked) => {
    setManualEntry(prev => ({
      ...prev,
      tour_types: checked
        ? [...prev.tour_types, tourType]
        : prev.tour_types.filter(t => t !== tourType)
    }));
  };

  // Open form for a specific tour source
  const handleSelectTourSource = (source) => {
    setSelectedTourSource(source);
    setManualEntry({
      company_name: '',
      date: getAdelaideDate(),
      time: '',
      number_of_people: '',
      crew_name: '',
      crew_no: '',
      staff_member: '',
      umoona_staff: '',
      notes: '',
      tour_source: source,
      tour_types: [],
      extra_tour_reason: ''
    });
    setEditingTourId(null);
    setShowManualEntryForm(true);
  };

  // Delete manual tour
  const handleDeleteManualTour = async (tourId) => {
    if (!window.confirm('Are you sure you want to delete this manual tour entry?')) {
      return;
    }

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/manual-tours/${tourId}`,
        {
          method: 'DELETE',
          headers
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete manual tour');
      }

      // Refresh manual tours list
      await fetchManualTours();
      
      // Refresh dashboard
      await fetchDashboardData(false);
      
    } catch (err) {
      setError(`Failed to delete manual tour: ${err.message}`);
    }
  };

  // Open new entry form with today's date (legacy - now using handleSelectTourSource)
  const handleOpenNewEntryForm = () => {
    setManualEntry({
      date: getAdelaideDate(),
      time: '',
      number_of_people: '',
      crew_name: '',
      staff_member: '',
      notes: '',
      tour_source: '',
      tour_types: [],
      extra_tour_reason: ''
    });
    setEditingTourId(null);
    setSelectedTourSource(null);
    setShowManualEntryForm(true);
  };

  // Close form and reset state
  const handleCloseForm = () => {
    setShowManualEntryForm(false);
    setSelectedTourSource(null);
    setEditingTourId(null);
    setManualEntry({
      company_name: '',
      date: getAdelaideDate(),
      time: '',
      number_of_people: '',
      crew_name: '',
      crew_no: '',
      staff_member: '',
      umoona_staff: '',
      notes: '',
      tour_source: '',
      tour_types: [],
      extra_tour_reason: ''
    });
  };

  // Inline editing handlers
  const startInlineEdit = (tourId, field, currentValue) => {
    setEditingField({ tourId, field });
    setEditingValue(currentValue || '');
  };

  const cancelInlineEdit = () => {
    setEditingField(null);
    setEditingValue('');
  };

  const saveInlineEdit = async () => {
    if (!editingField) return;

    const { tourId, field } = editingField;
    const tour = manualTours.find(t => t.id === tourId);
    if (!tour) return;

    setSavingField(editingField);

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      // Prepare updated data
      const updateData = {
        company_name: field === 'company_name' ? editingValue : (tour.company_name || ''),
        tour_date: field === 'tour_date' ? editingValue : tour.tour_date,
        tour_time: field === 'tour_time' ? editingValue : tour.tour_time,
        guest_count: field === 'guest_count' ? parseInt(editingValue) : tour.guest_count,
        crew_name: field === 'crew_name' ? editingValue : (tour.crew_name || ''),
        crew_no: field === 'crew_no' ? editingValue : (tour.crew_no || ''),
        staff_member: field === 'staff_member' ? editingValue : (tour.staff_member || ''),
        umoona_staff: field === 'umoona_staff' ? editingValue : (tour.umoona_staff || ''),
        notes: field === 'notes' ? editingValue : (tour.notes || ''),
        tour_types: field === 'tour_types'
          ? (Array.isArray(editingValue) ? editingValue : [])
          : (tour.tour_types || [])
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/manual-tours/${tourId}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify(updateData)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update tour');
      }

      // Refresh manual tours
      await fetchManualTours();
      
      // Refresh dashboard
      await fetchDashboardData(false);
      
      cancelInlineEdit();
    } catch (err) {
      setError(`Failed to update: ${err.message}`);
    } finally {
      setSavingField(null);
    }
  };

  const formatTimeDisplay = (time) => {
    if (!time) return 'Not set';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  // Staff assignment handlers
  const handleEditStaff = (timeSlot) => {
    setEditingStaff(timeSlot);
    setTempStaffName(staffAssignments[timeSlot] || '');
  };

  const handleSaveStaff = async (timeSlot) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      // Save to database
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/staff-assignments`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            tour_date: selectedDate,
            time_slot: timeSlot,
            staff_name: tempStaffName
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save staff assignment');
      }

      // Update local state
      setStaffAssignments({
        ...staffAssignments,
        [timeSlot]: tempStaffName
      });
      
      setEditingStaff(null);
      setTempStaffName('');
      
      // Refresh staff assignments from database
      await fetchStaffAssignments();
    } catch (err) {
      setError(`Failed to save staff assignment: ${err.message}`);
    }
  };

  const handleCancelStaffEdit = () => {
    setEditingStaff(null);
    setTempStaffName('');
  };

  const formatDate = (dateString) => {
    // Parse the date string and interpret it as Adelaide time
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    
    return date.toLocaleDateString('en-AU', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  const getAdelaideTime = () => {
    return new Date().toLocaleTimeString('en-AU', {
      timeZone: 'Australia/Adelaide',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusColor = (checkedIn) => {
    return checkedIn
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-blue-100 text-blue-800 border-blue-200';
  };

  // Format ticket breakdown for display
  const formatTicketBreakdown = (ticketsBreakdown) => {
    if (!ticketsBreakdown || ticketsBreakdown.length === 0) {
      return null;
    }

    const ticketLabels = {
      'adult': 'Adult',
      'child': 'Child',
      'family_3': 'Family (3)',
      'family_4': 'Family (4)',
      'family_5': 'Family (5)',
      'family_6': 'Family (6)',
      'family_7': 'Family (7)',
      'family_8': 'Family (8)'
    };

    return ticketsBreakdown.map((ticket, index) => {
      const label = ticketLabels[ticket.type] || ticket.type;
      const qty = ticket.quantity;
      // Pluralize for adult/child only
      const displayLabel = (ticket.type === 'adult' || ticket.type === 'child') && qty > 1
        ? `${label}s`
        : label;
      return (
        <span key={ticket.type}>
          {index > 0 && ', '}
          <span className="font-medium">{qty}</span> {displayLabel}
        </span>
      );
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <Button asChild variant="ghost" size="sm">
                <div onClick={() => navigate('/admin/dashboard')} className="cursor-pointer flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Dashboard
                </div>
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Live Tour Dashboard</h1>
                <p className="text-sm text-gray-500">Real-time tour booking management</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            <span className="ml-3 text-gray-600">Loading dashboard...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Session Expired Alert */}
      {error && error.includes('session has expired') && (
        <div className="bg-red-600 text-white px-4 py-3 text-center font-semibold shadow-lg">
          <div className="flex items-center justify-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </div>
      )}
      
      {/* Ultra-Modern Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Top Bar - Logo and Controls */}
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <img 
                src="/umoona-logo-transparent.png" 
                alt="Umoona Opal Mine" 
                className="h-10 sm:h-12 w-auto object-contain"
              />
            </div>
            
            <div className="flex items-center gap-1.5 sm:gap-2">
              {/* Connection & Update Status */}
              <div className="flex items-center gap-1.5">
                {connectionStatus === 'offline' && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-red-50 text-red-600 rounded-full text-xs font-medium">
                    <WifiOff className="w-3 h-3" />
                    <span className="hidden sm:inline">Offline</span>
                  </div>
                )}
                {connectionStatus === 'online' && error && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-600 rounded-full text-xs font-medium">
                    <AlertCircle className="w-3 h-3" />
                    <span className="hidden sm:inline">Issue</span>
                  </div>
                )}
                
                {lastUpdated && (
                  <div className="hidden sm:flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-600 rounded-full text-xs font-medium">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
                    <span className="text-[10px]">{lastUpdated.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                  </div>
                )}
              </div>
              
              {/* Date Picker */}
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-2.5 py-1.5 border border-gray-200 rounded-full text-xs font-medium focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent bg-gray-50 hover:bg-gray-100 transition-colors"
              />
              
              {/* Refresh Button */}
              <button
                onClick={() => {
                  console.log('Manual refresh triggered');
                  fetchDashboardData(true);
                  fetchManualTours();
                  fetchStaffAssignments();
                }} 
                disabled={loading}
                className="relative p-2 bg-slate-900 hover:bg-slate-800 text-white rounded-full transition-all shadow-sm hover:shadow-md disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                {!loading && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                  </span>
                )}
              </button>
              
              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="p-2 border border-gray-200 hover:border-red-300 hover:bg-red-50 text-gray-600 hover:text-red-600 rounded-full transition-all"
                title="Logout"
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          
          {/* Stats Bar */}
          <div className="py-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                {formatDate(selectedDate)}
              </h2>
              <div className="flex items-center gap-3 sm:gap-6">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-50 rounded-lg">
                    <Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-gray-900 leading-none">{dashboardData?.summary?.total_people || 0}</div>
                    <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">Guests</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-8 h-8 bg-emerald-50 rounded-lg">
                    <Calendar className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-gray-900 leading-none">{dashboardData?.summary?.total_bookings || 0}</div>
                    <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">Bookings</div>
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-2">
                  <div className="flex items-center justify-center w-8 h-8 bg-purple-50 rounded-lg">
                    <CheckCircle2 className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-gray-900 leading-none">{dashboardData?.summary?.total_checked_in || 0}</div>
                    <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">Checked In</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {error && (
          <Alert className="mb-6 border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-600">{error}</AlertDescription>
          </Alert>
        )}

        {/* Tour Time Slots */}
        {dashboardData && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-8">
            {dashboardData.available_times.map((timeSlot) => {
              const slotData = dashboardData.time_slots[timeSlot];
              return (
                <Card key={timeSlot} className="bg-white shadow-md hover:shadow-xl transition-shadow border border-gray-200 overflow-hidden">
                  <CardHeader className="bg-gradient-to-br from-slate-700 to-slate-800 text-white">
                    <CardTitle className="flex flex-col gap-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Clock className="w-5 h-5" />
                          <span className="text-xl font-bold">{slotData.time_display}</span>
                        </div>
                        <div className="bg-white text-slate-700 px-3 py-1 rounded-full font-semibold text-sm">
                          {slotData.total_bookings} bookings
                        </div>
                      </div>
                      
                      {/* STAFF Field - Editable */}
                      <div className="flex items-center gap-2">
                        <UserCheck className="w-4 h-4" />
                        <span className="text-sm font-semibold">STAFF:</span>
                        {editingStaff === timeSlot ? (
                          <div className="flex items-center gap-2 flex-1">
                            <input
                              type="text"
                              value={tempStaffName}
                              onChange={(e) => setTempStaffName(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') handleSaveStaff(timeSlot);
                              }}
                              className="flex-1 px-2 py-1 text-sm text-gray-900 rounded border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
                              placeholder="Enter staff name"
                              autoFocus
                            />
                            <button
                              onClick={() => handleSaveStaff(timeSlot)}
                              className="p-1 hover:bg-white/20 rounded"
                            >
                              <Save className="w-4 h-4" />
                            </button>
                            <button
                              onClick={handleCancelStaffEdit}
                              className="p-1 hover:bg-white/20 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div 
                            className="flex-1 flex items-center gap-2 cursor-pointer hover:bg-white/10 px-2 py-1 rounded transition-colors"
                            onClick={() => handleEditStaff(timeSlot)}
                          >
                            <span className="text-sm">
                              {staffAssignments[timeSlot] || 'Click to assign staff'}
                            </span>
                            <Edit2 className="w-3 h-3 opacity-70" />
                          </div>
                        )}
                      </div>
                      
                      {/* Booking Type Badges */}
                      <div className="flex items-center gap-2 mt-2">
                        <div className="bg-emerald-600 text-white px-2.5 py-1 rounded-full font-semibold text-xs">
                          {slotData.walkin_tickets || 0} Walk-in
                        </div>
                        <div className="bg-blue-600 text-white px-2.5 py-1 rounded-full font-semibold text-xs">
                          {slotData.online_tickets || 0} Online
                        </div>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="p-6">
                    {/* Walk-in Bookings - PRIMARY USE - Mobile Responsive */}
                    <div className="bg-white border-2 border-emerald-500 rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-shadow mb-6">
                      <div className="mb-3">
                        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-3 py-2 rounded-xl font-bold text-sm sm:text-base flex items-center gap-2 w-full justify-center shadow-md">
                          <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                          WALK-IN BOOKINGS
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row items-stretch gap-2 sm:gap-3">
                        <Button
                          variant="outline"
                          size="lg"
                          onClick={() => handleManualAdjustment(timeSlot, -1, 'walkin')}
                          disabled={slotData.walkin_tickets === 0}
                          className="w-full sm:w-auto bg-white hover:bg-rose-50 border-2 border-rose-400 text-rose-600 hover:text-rose-700 font-semibold h-12 px-4 rounded-xl transition-all"
                          data-testid={`minus-walkin-${timeSlot}`}
                        >
                          <MinusCircle className="w-6 h-6" />
                        </Button>
                        <Button
                          size="lg"
                          onClick={() => handleManualAdjustment(timeSlot, 1, 'walkin')}
                          disabled={slotData.capacity && slotData.capacity.used >= slotData.capacity.total}
                          className="w-full sm:flex-1 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold h-12 shadow-md hover:shadow-lg rounded-xl transition-all text-sm sm:text-base px-3"
                          data-testid={`plus-walkin-${timeSlot}`}
                        >
                          <PlusCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 flex-shrink-0" />
                          <span className="whitespace-nowrap">Add Walk-in</span>
                        </Button>
                      </div>
                      <div className="text-xs sm:text-sm text-green-800 font-medium mt-3 bg-white/60 rounded-lg p-2">
                        {slotData.capacity ? (
                          <>
                            ✓ {slotData.capacity.walkin_available} spots available
                          </>
                        ) : (
                          `${30 - (slotData.total_people || 0)} spots remaining`
                        )}
                      </div>
                    </div>

                    {/* Customer List - Only Online Bookings (MOVED TO TOP) */}
                    {slotData.customers.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 bg-blue-50 rounded-lg border border-blue-200">
                        <Users className="w-12 h-12 mx-auto mb-4 text-blue-300" />
                        <p className="font-medium text-blue-900">No Online Bookings</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <h4 className="font-semibold text-gray-900 flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-lg">
                          <Users className="w-4 h-4 text-blue-600" />
                          Online Customers ({slotData.customers.length})
                        </h4>
                        
                        {slotData.customers.map((customer) => (
                          <div 
                            key={customer.booking_id} 
                            className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex-1">
                                <h5 className="font-medium text-gray-900">
                                  {customer.customer_name}
                                </h5>
                                <p className="text-sm text-gray-600">
                                  {customer.customer_email}
                                </p>
                                {customer.visiting_from && (
                                  <p className="text-sm text-blue-600 font-medium">
                                    From: {customer.visiting_from}
                                  </p>
                                )}
                                <p className="text-sm text-gray-500">
                                  Ref: {customer.booking_reference}
                                </p>
                              </div>
                              
                              <div className="text-right">
                                <Badge className={getStatusColor(customer.checked_in)}>
                                  {customer.checked_in ? 'Checked In' : 'Pending'}
                                </Badge>
                              </div>
                            </div>
                            
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-gray-600">
                                {customer.tickets_breakdown && customer.tickets_breakdown.length > 0 ? (
                                  formatTicketBreakdown(customer.tickets_breakdown)
                                ) : (
                                  <>
                                    <span className="font-medium">{customer.guest_count}</span> guests,
                                    <span className="font-medium"> {customer.ticket_count}</span> tickets
                                  </>
                                )}
                              </div>
                              
                              {!customer.checked_in && (
                                <Button
                                  size="sm"
                                  onClick={() => handleCheckIn(customer.booking_id, customer.customer_name)}
                                  disabled={checkingIn.has(customer.booking_id)}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  {checkingIn.has(customer.booking_id) ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                                      Checking In...
                                    </>
                                  ) : (
                                    <>
                                      <UserCheck className="w-3 h-3 mr-1" />
                                      Check In
                                    </>
                                  )}
                                </Button>
                              )}
                              
                              {customer.checked_in && customer.checked_in_at && (
                                <div className="text-xs text-gray-500">
                                  Checked in at {new Date(customer.checked_in_at).toLocaleTimeString('en-AU', { timeZone: 'Australia/Adelaide' })}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Total Capacity Summary (MOVED HERE - RIGHT AFTER CUSTOMER LIST) */}
                    <div className="bg-gradient-to-br from-slate-50 to-slate-100 border-2 border-slate-300 rounded-2xl p-5 text-center mt-6 shadow-sm">
                      <div className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Total Tour Capacity</div>
                      <div className="text-4xl font-bold text-slate-900 mb-3">
                        {slotData.total_people || 0} / 30
                      </div>
                      <div className="text-sm text-slate-700 font-semibold">
                        {30 - (slotData.total_people || 0)} spots remaining
                      </div>
                    </div>

                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* No tours available */}
        {dashboardData && dashboardData.available_times.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Tours Available</h3>
              <p className="text-gray-500">
                No underground mine tours are scheduled for {formatDate(selectedDate)}
              </p>
            </CardContent>
          </Card>
        )}

        {/* MODERN Manual Tour Management Section */}
        <Card className="mt-8 border-2 border-slate-200 shadow-lg overflow-hidden">
          <CardHeader className="bg-gradient-to-br from-slate-700 to-slate-900 text-white pb-6">
            <CardTitle className="flex flex-col gap-4">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl shadow-md">
                    <Clock className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-2xl font-bold">Manual Tour Log</h3>
                      <span className="flex items-center gap-1.5 px-3 py-1 bg-emerald-500/20 backdrop-blur-sm border border-emerald-400/30 rounded-full text-xs font-semibold text-emerald-100">
                        <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                        Live Sync (3s)
                      </span>
                    </div>
                    <p className="text-slate-200 text-sm mt-1.5">Log tours from Oasis, Noble, or manual entries</p>
                  </div>
                </div>
                {showManualEntryForm && (
                  <Button
                    onClick={handleCloseForm}
                    className="bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 text-white shadow-md hover:shadow-lg transition-all"
                    size="lg"
                  >
                    <X className="w-5 h-5 mr-2" />
                    Close Form
                  </Button>
                )}
              </div>

              {/* Three Tour Source Buttons */}
              {!showManualEntryForm && (
                <div className="flex flex-wrap gap-3 mt-2">
                  <Button
                    onClick={() => handleSelectTourSource('oasis')}
                    className="flex-1 min-w-[140px] h-auto py-4 flex flex-col items-center gap-2 bg-emerald-500/20 hover:bg-emerald-500/40 backdrop-blur-sm border-2 border-emerald-400/50 text-white shadow-md hover:shadow-lg transition-all"
                  >
                    <Building2 className="w-6 h-6" />
                    <span className="font-semibold">Oasis Tour</span>
                  </Button>

                  <Button
                    onClick={() => handleSelectTourSource('noble')}
                    className="flex-1 min-w-[140px] h-auto py-4 flex flex-col items-center gap-2 bg-sky-500/20 hover:bg-sky-500/40 backdrop-blur-sm border-2 border-sky-400/50 text-white shadow-md hover:shadow-lg transition-all"
                  >
                    <Building2 className="w-6 h-6" />
                    <span className="font-semibold">Noble Tour</span>
                  </Button>

                  <Button
                    onClick={() => handleSelectTourSource('manual')}
                    className="flex-1 min-w-[140px] h-auto py-4 flex flex-col items-center gap-2 bg-purple-500/20 hover:bg-purple-500/40 backdrop-blur-sm border-2 border-purple-400/50 text-white shadow-md hover:shadow-lg transition-all"
                  >
                    <PlusCircle className="w-6 h-6" />
                    <span className="font-semibold">Manual Entry</span>
                  </Button>
                </div>
              )}
            </CardTitle>
          </CardHeader>
          
          {showManualEntryForm && selectedTourSource && (
            <CardContent className={`pt-6 pb-8 bg-gradient-to-br ${
              selectedTourSource === 'oasis' ? 'from-emerald-50 to-emerald-100/30' :
              selectedTourSource === 'noble' ? 'from-sky-50 to-sky-100/30' :
              'from-purple-50 to-purple-100/30'
            }`}>
              {/* Form Header Badge */}
              <div className="mb-6">
                <Badge className={`text-sm px-4 py-2 ${
                  selectedTourSource === 'oasis' ? 'bg-emerald-600 text-white' :
                  selectedTourSource === 'noble' ? 'bg-sky-600 text-white' :
                  'bg-purple-600 text-white'
                }`}>
                  {tourSourceConfig[selectedTourSource]?.label || 'Tour Entry'}
                </Badge>
              </div>

              <form onSubmit={handleManualEntrySubmit} className="space-y-4 max-w-5xl">
                {/* Row 1: Company Name (full width) */}
                <div className="space-y-2">
                  <label className="flex items-center gap-1 text-sm font-bold text-gray-700">
                    <Building2 className="w-4 h-4 text-indigo-600" />
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={manualEntry.company_name}
                    onChange={(e) => setManualEntry({...manualEntry, company_name: e.target.value})}
                    className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white shadow-sm transition-all text-sm"
                    placeholder="Company name"
                  />
                </div>

                {/* Row 2: Date, Time, Guests, Crew No. */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <label className="flex items-center gap-1 text-sm font-bold text-gray-700">
                      <Calendar className="w-4 h-4 text-indigo-600" />
                      Date *
                    </label>
                    <input
                      type="date"
                      value={manualEntry.date}
                      onChange={(e) => setManualEntry({...manualEntry, date: e.target.value})}
                      className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white shadow-sm transition-all text-sm"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center gap-1 text-sm font-bold text-gray-700">
                      <Clock className="w-4 h-4 text-indigo-600" />
                      Time *
                    </label>
                    <select
                      value={manualEntry.time}
                      onChange={(e) => setManualEntry({...manualEntry, time: e.target.value})}
                      className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white shadow-sm transition-all text-sm"
                      required
                    >
                      <option value="">Select time</option>
                      {timeOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center gap-1 text-sm font-bold text-gray-700">
                      <Users className="w-4 h-4 text-indigo-600" />
                      Guests *
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="500"
                      value={manualEntry.number_of_people}
                      onChange={(e) => setManualEntry({...manualEntry, number_of_people: e.target.value})}
                      className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white shadow-sm transition-all text-sm"
                      placeholder="#"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center gap-1 text-sm font-bold text-gray-700">
                      <Users className="w-4 h-4 text-indigo-600" />
                      Crew No.
                    </label>
                    <input
                      type="text"
                      value={manualEntry.crew_no}
                      onChange={(e) => setManualEntry({...manualEntry, crew_no: e.target.value})}
                      className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white shadow-sm transition-all text-sm"
                      placeholder="#"
                    />
                  </div>
                </div>

                {/* Row 3: Crew Name + Umoona Staff (side by side) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="flex items-center gap-1 text-sm font-bold text-gray-700">
                      <Users className="w-4 h-4 text-indigo-600" />
                      Crew Name
                    </label>
                    <input
                      type="text"
                      value={manualEntry.crew_name}
                      onChange={(e) => setManualEntry({...manualEntry, crew_name: e.target.value})}
                      className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white shadow-sm transition-all text-sm"
                      placeholder="Group/crew name"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center gap-1 text-sm font-bold text-gray-700">
                      <UserCheck className="w-4 h-4 text-indigo-600" />
                      Umoona Staff
                    </label>
                    <input
                      type="text"
                      value={manualEntry.umoona_staff}
                      onChange={(e) => setManualEntry({...manualEntry, umoona_staff: e.target.value})}
                      className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white shadow-sm transition-all text-sm"
                      placeholder="Umoona staff member name"
                    />
                  </div>
                </div>

                {/* Tour Type Checkboxes */}
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                    <ClipboardCheck className="w-4 h-4 text-indigo-600" />
                    Tour Type
                  </label>
                  <div className="flex flex-wrap gap-4">
                    {tourTypeOptions.map(opt => (
                      <label key={opt.value} className="flex items-center gap-2 cursor-pointer bg-white px-4 py-2 rounded-lg border-2 border-gray-200 hover:border-indigo-300 transition-all">
                        <input
                          type="checkbox"
                          checked={manualEntry.tour_types.includes(opt.value)}
                          onChange={(e) => handleTourTypeChange(opt.value, e.target.checked)}
                          className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Extra Tour Reason - Only for Manual entries */}
                {selectedTourSource === 'manual' && (
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                      <AlertCircle className="w-4 h-4 text-purple-600" />
                      Reason for Extra Tour *
                    </label>
                    <textarea
                      value={manualEntry.extra_tour_reason}
                      onChange={(e) => setManualEntry({...manualEntry, extra_tour_reason: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-purple-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white shadow-sm transition-all resize-none"
                      rows="2"
                      placeholder="Why was this extra tour needed? (e.g., large group, special request)"
                      required
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                    <AlertCircle className="w-4 h-4 text-indigo-600" />
                    Notes (Optional)
                  </label>
                  <textarea
                    value={manualEntry.notes}
                    onChange={(e) => setManualEntry({...manualEntry, notes: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white shadow-sm transition-all resize-none"
                    rows="2"
                    placeholder="Add any additional details about this tour..."
                  />
                </div>
                
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-6">
                  <Button
                    type="submit"
                    disabled={submittingManualEntry}
                    className={`text-white px-8 py-3 text-base font-semibold shadow-lg hover:shadow-xl transition-all ${
                      selectedTourSource === 'oasis' ? 'bg-emerald-600 hover:bg-emerald-700' :
                      selectedTourSource === 'noble' ? 'bg-sky-600 hover:bg-sky-700' :
                      'bg-purple-600 hover:bg-purple-700'
                    }`}
                    size="lg"
                  >
                    {submittingManualEntry ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5 mr-2" />
                        Save {tourSourceConfig[selectedTourSource]?.label || 'Entry'}
                      </>
                    )}
                  </Button>

                  <Button
                    type="button"
                    onClick={handleCloseForm}
                    variant="outline"
                    className="border-2 border-gray-300 hover:border-gray-400"
                    size="lg"
                  >
                    <X className="w-5 h-5 mr-2" />
                    Cancel
                  </Button>

                  <p className="text-sm text-gray-600 sm:ml-auto text-center sm:text-left">
                    * Required fields
                  </p>
                </div>
              </form>
            </CardContent>
          )}

          {/* Manual Tours List - Professional Table Layout */}
          {manualTours.length > 0 && (
            <CardContent className="border-t-4 border-indigo-100 bg-gray-50/50 py-8">
              <div className="flex items-center justify-between mb-6">
                <h4 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                  <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-2.5 rounded-lg">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  Logged Entries ({manualTours.length})
                </h4>
                <Badge className="bg-indigo-100 text-indigo-700 px-4 py-2 text-sm font-semibold">
                  Click cells to edit
                </Badge>
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-slate-700 to-slate-800 text-white">
                      <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider">Source</th>
                      <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider">Company</th>
                      <th className="px-2 py-3 text-left text-xs font-bold uppercase tracking-wider">Time</th>
                      <th className="px-2 py-3 text-left text-xs font-bold uppercase tracking-wider">Date</th>
                      <th className="px-2 py-3 text-center text-xs font-bold uppercase tracking-wider">Guests</th>
                      <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider">Crew</th>
                      <th className="px-2 py-3 text-center text-xs font-bold uppercase tracking-wider">Crew No.</th>
                      <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider">Services</th>
                      <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider">Umoona Staff</th>
                      <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider">Notes</th>
                      <th className="px-2 py-3 text-center text-xs font-bold uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[...manualTours]
                      .sort((a, b) => {
                        const timeA = a.tour_time || '00:00';
                        const timeB = b.tour_time || '00:00';
                        return timeA.localeCompare(timeB);
                      })
                      .map((tour) => {
                        const isEditingThis = (field) => editingField?.tourId === tour.id && editingField?.field === field;
                        const isSavingThis = (field) => savingField?.tourId === tour.id && savingField?.field === field;
                        const tourSource = tour.tour_source || 'manual';

                        return (
                          <tr key={tour.id} className="hover:bg-gray-50 transition-colors">
                            {/* Source Column */}
                            <td className="px-3 py-3">
                              <Badge className={`text-xs font-semibold ${
                                tourSource === 'oasis' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' :
                                tourSource === 'noble' ? 'bg-sky-100 text-sky-700 border border-sky-200' :
                                'bg-purple-100 text-purple-700 border border-purple-200'
                              }`}>
                                {tourSource === 'oasis' ? 'Oasis' : tourSource === 'noble' ? 'Noble' : 'Manual'}
                              </Badge>
                            </td>

                            {/* Company Name Column */}
                            <td className="px-3 py-3">
                              {isEditingThis('company_name') ? (
                                <input
                                  type="text"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={saveInlineEdit}
                                  onKeyDown={(e) => { if (e.key === 'Enter') saveInlineEdit(); if (e.key === 'Escape') cancelInlineEdit(); }}
                                  className="w-full px-2 py-1 border-2 border-indigo-500 rounded text-sm focus:outline-none"
                                  placeholder="Company name"
                                  autoFocus
                                  disabled={isSavingThis('company_name')}
                                />
                              ) : (
                                <button
                                  onClick={() => startInlineEdit(tour.id, 'company_name', tour.company_name || '')}
                                  className="text-sm text-gray-700 hover:bg-indigo-50 px-2 py-1 rounded transition-colors cursor-pointer"
                                >
                                  {tour.company_name || <span className="text-gray-400">-</span>}
                                </button>
                              )}
                            </td>

                            {/* Time Column */}
                            <td className="px-2 py-3">
                              {isEditingThis('tour_time') ? (
                                <select
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={saveInlineEdit}
                                  className="w-full px-2 py-1 border-2 border-indigo-500 rounded text-sm focus:outline-none"
                                  autoFocus
                                  disabled={isSavingThis('tour_time')}
                                >
                                  {timeOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              ) : (
                                <button
                                  onClick={() => startInlineEdit(tour.id, 'tour_time', tour.tour_time)}
                                  className="font-bold text-indigo-600 hover:bg-indigo-50 px-2 py-1 rounded transition-colors cursor-pointer"
                                >
                                  {formatTimeDisplay(tour.tour_time)}
                                </button>
                              )}
                            </td>

                            {/* Date Column */}
                            <td className="px-2 py-3">
                              {isEditingThis('tour_date') ? (
                                <input
                                  type="date"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={saveInlineEdit}
                                  onKeyDown={(e) => { if (e.key === 'Enter') saveInlineEdit(); if (e.key === 'Escape') cancelInlineEdit(); }}
                                  className="w-full px-2 py-1 border-2 border-indigo-500 rounded text-sm focus:outline-none"
                                  autoFocus
                                  disabled={isSavingThis('tour_date')}
                                />
                              ) : (
                                <button
                                  onClick={() => startInlineEdit(tour.id, 'tour_date', tour.tour_date)}
                                  className="text-sm text-gray-700 hover:bg-indigo-50 px-2 py-1 rounded transition-colors cursor-pointer"
                                >
                                  {formatDateDisplay(tour.tour_date)}
                                </button>
                              )}
                            </td>

                            {/* Guests Column */}
                            <td className="px-2 py-3 text-center">
                              {isEditingThis('guest_count') ? (
                                <input
                                  type="number"
                                  min="1"
                                  max="500"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={saveInlineEdit}
                                  onKeyDown={(e) => { if (e.key === 'Enter') saveInlineEdit(); if (e.key === 'Escape') cancelInlineEdit(); }}
                                  className="w-16 px-2 py-1 border-2 border-indigo-500 rounded text-sm text-center focus:outline-none"
                                  autoFocus
                                  disabled={isSavingThis('guest_count')}
                                />
                              ) : (
                                <button
                                  onClick={() => startInlineEdit(tour.id, 'guest_count', tour.guest_count.toString())}
                                  className="font-semibold text-gray-900 hover:bg-indigo-50 px-2 py-1 rounded transition-colors cursor-pointer"
                                >
                                  {tour.guest_count}
                                </button>
                              )}
                            </td>

                            {/* Crew Name Column */}
                            <td className="px-3 py-3">
                              {isEditingThis('crew_name') ? (
                                <input
                                  type="text"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={saveInlineEdit}
                                  onKeyDown={(e) => { if (e.key === 'Enter') saveInlineEdit(); if (e.key === 'Escape') cancelInlineEdit(); }}
                                  className="w-full px-2 py-1 border-2 border-indigo-500 rounded text-sm focus:outline-none"
                                  placeholder="Crew name"
                                  autoFocus
                                  disabled={isSavingThis('crew_name')}
                                />
                              ) : (
                                <button
                                  onClick={() => startInlineEdit(tour.id, 'crew_name', tour.crew_name || '')}
                                  className="text-sm text-gray-700 hover:bg-indigo-50 px-2 py-1 rounded transition-colors cursor-pointer"
                                >
                                  {tour.crew_name || <span className="text-gray-400">-</span>}
                                </button>
                              )}
                            </td>

                            {/* Crew No. Column */}
                            <td className="px-2 py-3 text-center">
                              {isEditingThis('crew_no') ? (
                                <input
                                  type="text"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={saveInlineEdit}
                                  onKeyDown={(e) => { if (e.key === 'Enter') saveInlineEdit(); if (e.key === 'Escape') cancelInlineEdit(); }}
                                  className="w-16 px-2 py-1 border-2 border-indigo-500 rounded text-sm text-center focus:outline-none"
                                  placeholder="#"
                                  autoFocus
                                  disabled={isSavingThis('crew_no')}
                                />
                              ) : (
                                <button
                                  onClick={() => startInlineEdit(tour.id, 'crew_no', tour.crew_no || '')}
                                  className="text-sm text-gray-700 hover:bg-indigo-50 px-2 py-1 rounded transition-colors cursor-pointer"
                                >
                                  {tour.crew_no || <span className="text-gray-400">-</span>}
                                </button>
                              )}
                            </td>

                            {/* Services/Tour Type Column */}
                            <td className="px-3 py-3">
                              {isEditingThis('tour_types') ? (
                                <div
                                  className="border-2 border-indigo-500 rounded p-2 bg-white min-w-[260px]"
                                  onKeyDown={(e) => { if (e.key === 'Escape') cancelInlineEdit(); }}
                                >
                                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mb-2">
                                    {tourTypeOptions.map(opt => {
                                      const arr = Array.isArray(editingValue) ? editingValue : [];
                                      const checked = arr.includes(opt.value);
                                      return (
                                        <label
                                          key={opt.value}
                                          className="flex items-center gap-1.5 text-xs cursor-pointer"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={(e) => {
                                              const isChecked = e.target.checked;
                                              setEditingValue(prev => {
                                                const cur = Array.isArray(prev) ? prev : [];
                                                return isChecked
                                                  ? [...cur, opt.value]
                                                  : cur.filter(v => v !== opt.value);
                                              });
                                            }}
                                            disabled={isSavingThis('tour_types')}
                                            className="w-3.5 h-3.5 text-indigo-600 rounded focus:ring-indigo-500"
                                          />
                                          <span className="text-gray-700">{opt.label}</span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                  <div className="flex gap-2 justify-end pt-1.5 border-t border-gray-200">
                                    <button
                                      type="button"
                                      onClick={cancelInlineEdit}
                                      disabled={isSavingThis('tour_types')}
                                      className="text-xs text-gray-600 hover:text-gray-900 px-2 py-1"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      onClick={saveInlineEdit}
                                      disabled={isSavingThis('tour_types')}
                                      className="text-xs bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white px-3 py-1 rounded"
                                    >
                                      {isSavingThis('tour_types') ? 'Saving…' : 'Done'}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => startInlineEdit(tour.id, 'tour_types', tour.tour_types || [])}
                                  className="flex flex-wrap gap-1 hover:bg-indigo-50 px-2 py-1 rounded transition-colors cursor-pointer text-left min-w-[80px]"
                                  title="Click to edit services"
                                >
                                  {tour.tour_types && tour.tour_types.length > 0 ? (
                                    tour.tour_types.map(t => (
                                      <span key={t} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                        {tourTypeOptions.find(o => o.value === t)?.label || t}
                                      </span>
                                    ))
                                  ) : (
                                    <span className="text-xs text-gray-400">-</span>
                                  )}
                                </button>
                              )}
                            </td>

                            {/* Umoona Staff Column */}
                            <td className="px-3 py-3">
                              {isEditingThis('umoona_staff') ? (
                                <input
                                  type="text"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={saveInlineEdit}
                                  onKeyDown={(e) => { if (e.key === 'Enter') saveInlineEdit(); if (e.key === 'Escape') cancelInlineEdit(); }}
                                  className="w-full px-2 py-1 border-2 border-indigo-500 rounded text-sm focus:outline-none"
                                  placeholder="Umoona staff"
                                  autoFocus
                                  disabled={isSavingThis('umoona_staff')}
                                />
                              ) : (
                                <button
                                  onClick={() => startInlineEdit(tour.id, 'umoona_staff', tour.umoona_staff || '')}
                                  className="text-sm text-gray-700 hover:bg-indigo-50 px-2 py-1 rounded transition-colors cursor-pointer"
                                >
                                  {tour.umoona_staff || <span className="text-gray-400">-</span>}
                                </button>
                              )}
                            </td>

                            {/* Notes Column */}
                            <td className="px-3 py-3 max-w-[200px]">
                              {isEditingThis('notes') ? (
                                <textarea
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={saveInlineEdit}
                                  onKeyDown={(e) => { if (e.key === 'Escape') cancelInlineEdit(); }}
                                  className="w-full px-2 py-1 border-2 border-indigo-500 rounded text-sm focus:outline-none resize-none"
                                  rows="2"
                                  autoFocus
                                  disabled={isSavingThis('notes')}
                                />
                              ) : (
                                <button
                                  onClick={() => startInlineEdit(tour.id, 'notes', tour.notes || '')}
                                  className="text-left text-sm text-gray-600 hover:bg-indigo-50 px-2 py-1 rounded transition-colors cursor-pointer truncate max-w-[180px] block"
                                  title={tour.notes || 'Click to add notes'}
                                >
                                  {tour.notes ? (
                                    <span className="italic">{tour.notes.length > 30 ? tour.notes.substring(0, 30) + '...' : tour.notes}</span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </button>
                              )}
                            </td>

                            {/* Actions Column */}
                            <td className="px-2 py-3 text-center">
                              <Button
                                onClick={() => handleDeleteManualTour(tour.id)}
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2"
                                title="Delete entry"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {[...manualTours]
                  .sort((a, b) => {
                    const timeA = a.tour_time || '00:00';
                    const timeB = b.tour_time || '00:00';
                    return timeA.localeCompare(timeB);
                  })
                  .map((tour) => {
                    const tourSource = tour.tour_source || 'manual';
                    return (
                      <div
                        key={tour.id}
                        className={`bg-white rounded-xl border-2 overflow-hidden ${
                          tourSource === 'oasis' ? 'border-emerald-200' :
                          tourSource === 'noble' ? 'border-sky-200' :
                          'border-purple-200'
                        }`}
                      >
                        <div className={`px-4 py-2 flex items-center justify-between ${
                          tourSource === 'oasis' ? 'bg-emerald-50' :
                          tourSource === 'noble' ? 'bg-sky-50' :
                          'bg-purple-50'
                        }`}>
                          <Badge className={`text-xs font-semibold ${
                            tourSource === 'oasis' ? 'bg-emerald-600 text-white' :
                            tourSource === 'noble' ? 'bg-sky-600 text-white' :
                            'bg-purple-600 text-white'
                          }`}>
                            {tourSource === 'oasis' ? 'Oasis' : tourSource === 'noble' ? 'Noble' : 'Manual'}
                          </Badge>
                          <span className="font-bold text-lg text-gray-900">{formatTimeDisplay(tour.tour_time)}</span>
                        </div>
                        <div className="p-4">
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            {tour.company_name && (
                              <div className="col-span-2">
                                <span className="text-gray-500">Company:</span>
                                <span className="ml-2 font-medium">{tour.company_name}</span>
                              </div>
                            )}
                            <div>
                              <span className="text-gray-500">Date:</span>
                              <span className="ml-2 font-medium">{formatDateDisplay(tour.tour_date)}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Guests:</span>
                              <span className="ml-2 font-bold text-indigo-600">{tour.guest_count}</span>
                            </div>
                            {tour.crew_name && (
                              <div>
                                <span className="text-gray-500">Crew:</span>
                                <span className="ml-2 font-medium">{tour.crew_name}</span>
                              </div>
                            )}
                            {tour.crew_no && (
                              <div>
                                <span className="text-gray-500">Crew No.:</span>
                                <span className="ml-2 font-medium">{tour.crew_no}</span>
                              </div>
                            )}
                            {tour.umoona_staff && (
                              <div className="col-span-2">
                                <span className="text-gray-500">Umoona Staff:</span>
                                <span className="ml-2 font-medium">{tour.umoona_staff}</span>
                              </div>
                            )}
                            {tour.notes && (
                              <div className="col-span-2">
                                <span className="text-gray-500">Notes:</span>
                                <span className="ml-2 text-gray-600 italic">{tour.notes}</span>
                              </div>
                            )}
                          </div>
                          <div className="mt-3 pt-3 border-t border-gray-100 flex justify-end">
                            <Button
                              onClick={() => handleDeleteManualTour(tour.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          )}

          {manualTours.length === 0 && !showManualEntryForm && (
            <CardContent className="text-center py-12 bg-gradient-to-br from-gray-50 to-indigo-50/30">
              <div className="bg-gradient-to-br from-indigo-500 to-purple-600 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-1">No Entries Yet</h4>
              <p className="text-gray-500 text-sm">
                No manual tour entries logged for {formatDate(selectedDate)}.
              </p>
            </CardContent>
          )}
        </Card>
        
        {/* Footer Info */}
        <div className="mt-8 text-center text-sm text-gray-500">
          Dashboard refreshes automatically every 10 seconds • Last updated: {getAdelaideTime()}
        </div>
      </div>
    </div>
  );
};

export default AdminLiveDashboardPage;